import { useCallback, useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"

import { ChatState, Message } from "@/types/chatbot"
import { systemConfig } from "@/config/site"
import { fetchUserConversations } from "@/lib/api/assistant"
import { apiFetch } from "@/lib/utils"

export const useChat = () => {
  const [chatState, setChatState] = useState<ChatState>({
    isOpen: false,
    messages: [],
  })

  const {
    data: initialMessages,
    isLoading,
    isError,
  } = useQuery<Message[]>({
    queryKey: ["userConversations"],
    queryFn: fetchUserConversations,
  })

  useEffect(() => {
    if (initialMessages) {
      setChatState((prev) => ({
        ...prev,
        messages: initialMessages.map((msg) => ({
          ...msg,
          created_at: msg.created_at || new Date().toISOString(),
          updated_at: msg.updated_at || new Date().toISOString(),
        })),
      }))
    }
  }, [initialMessages])

  const toggleChat = useCallback(() => {
    setChatState((prev) => ({ ...prev, isOpen: !prev.isOpen }))
  }, [])

  const addMessage = useCallback(
    async (content: string, sender: "user" | "bot") => {
      const newMessage: Message = {
        id: Date.now().toString(),
        content,
        sender,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }))

      if (sender === "user") {
        try {
          const response = await apiFetch("himo-assistant/gemini-chatbot/", {
            method: "POST",
            body: JSON.stringify({
              message: content,
              hirayatech_system_id: systemConfig.hirayaSystemId,
            }),
          })

          const responseData = await response.json()

          const botReply: Message = {
            id: (Date.now() + 1).toString(),
            content: responseData.reply,
            sender: "bot",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }

          setChatState((prev) => ({
            ...prev,
            messages: [...prev.messages, botReply],
          }))
        } catch (error) {
          console.error("Error fetching bot reply:", error)
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            content: "Sorry, I encountered an error. Please try again.",
            sender: "bot",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
          setChatState((prev) => ({
            ...prev,
            messages: [...prev.messages, errorMessage],
          }))
        }
      }
    },
    [],
  )

  const addMessages = useCallback((newMessages: Message[]) => {
    setChatState((prev) => ({
      ...prev,
      messages: [
        ...prev.messages,
        ...newMessages.map((msg) => ({
          ...msg,
          created_at: msg.created_at || new Date().toISOString(),
          updated_at: msg.updated_at || new Date().toISOString(),
        })),
      ],
    }))
  }, [])

  return {
    chatState,
    toggleChat,
    addMessage,
    addMessages,
    isLoading,
    isError,
  }
}
